import { useParams, useNavigate, Link } from 'react-router-dom'
import { useState } from 'react'
import {
  ArrowLeft,
  User,
  TrendingUp,
  Pencil,
  Save,
  X,
  Calendar,
  CheckCircle2,
  FileText,
  History,
  Plus,
  Mail,
  Send,
  MessageSquareText,
  Trash2,
  LifeBuoy,
  Headphones,
} from 'lucide-react'
import { ClickToCallButton } from '@/components/ClickToCallButton'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/hooks/use-toast'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { useRecordBreadcrumb } from '@/contexts/BreadcrumbContext'
import { usePicklistOptions } from '@/hooks/use-picklists'
import { leadFieldPerm, leadRoleOf } from '@/lib/leadFieldPermissions'
import { useClickToEdit } from '@/hooks/use-click-to-edit'
import { getBantStatusVariant, type BantCriterion } from '@/lib/bant-variants'
import { HistoryTimeline } from '@/components/HistoryTimeline'
import { ActivityTimeline } from '@/components/ActivityTimeline'
import { RelatedEmails } from '@/components/RelatedEmails'
import { LeadStageProgress } from '@/components/LeadStageProgress'
import { ConvertLeadModal } from '@/components/ConvertLeadModal'
import { EditActionBar } from '@/components/EditActionBar'
import { DisqualifyLeadModal } from '@/components/DisqualifyLeadModal'
import { CreateTaskModal } from '@/components/CreateTaskModal'
import { CreateEventModal } from '@/components/CreateEventModal'
import { CreateNoteModal } from '@/components/CreateNoteModal'
import { NoteDetailModal } from '@/components/NoteDetailModal'
import { EditNoteModal } from '@/components/EditNoteModal'
import { TaskDetailModal } from '@/components/TaskDetailModal'
import { useDeleteNote as useDeleteNoteMutation, useDeleteNoteSession } from '@/hooks/use-api'
import type { Note } from '@/types'
import type { NoteSession } from '@/types/noteSession'
import { LeadScoreCard } from '@/components/LeadScoreCard'
import { MailchimpContactSection } from '@/components/MailchimpContactSection'
import { NextBestAction } from '@/components/NextBestAction'
import { ComposeEmailModal } from '@/components/ComposeEmailModal'
import { NoteSessionPanel } from '@/components/notes/NoteSessionPanel'
import { MergedNotesList } from '@/components/notes/MergedNotesList'
import {
  useLeadDetail,
  useUpdateLead,
  useUsers,
  useLeadScore,
  useDeleteLead,
  useNoteSessionsByLead,
  useTickets,
} from '@/hooks/use-api'
import { ticketStatusVariants, ticketPriorityVariants } from '@/lib/ticket-variants'
import { formatDate } from '@/lib/utils'
import { useUser } from '@/contexts/UserContext'
import type { Lead } from '@/types'
import { CategoryBadge } from '@/components/ui/CategoryBadge'
import { CompletenessIndicator } from '@/components/CompletenessIndicator'
import { calculateCompleteness, getImportantFields } from '@/utils/dataQuality'
import { explainError } from '@/lib/explain-error'

export default function LeadDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()

  const { impersonatedUser, currentUser } = useUser()
  // Lead field-permission role (honour Admin preview-as). v1.1: Owner/Status/
  // Rating/BANT are Sales+Admin; Marketing Status is Marketing/BDR/Admin.
  const leadRole = leadRoleOf(
    (impersonatedUser?.user_type ?? currentUser?.user_type) as string | undefined
  )
  const canChangeOwner = leadFieldPerm(leadRole, 'owner_id') === 'edit' && !impersonatedUser
  // Only Admin/Super Admin can delete (for cleanup); same gate as owner change.
  const canDelete = canChangeOwner
  const deleteLeadMutation = useDeleteLead()
  const deleteNoteMutation = useDeleteNoteMutation(id)
  const deleteSessionMutation = useDeleteNoteSession()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const { data, isLoading, error } = useLeadDetail(id!)
  useDocumentTitle(
    data?.lead
      ? `${`${data.lead.first_name || ''} ${data.lead.last_name || ''}`.trim()}${data.lead.company ? ` – ${data.lead.company}` : ''} | Lead`
      : undefined
  )
  useRecordBreadcrumb(
    data?.lead ? `${data.lead.first_name || ''} ${data.lead.last_name || ''}`.trim() : undefined
  )
  const { data: usersData } = useUsers({ limit: 10000 })
  const isDisqualified = data?.lead?.status === 'Disqualified'
  const { data: leadScore, isLoading: leadScoreLoading } = useLeadScore(id!, !isDisqualified)
  const { data: noteSessions } = useNoteSessionsByLead(id!)
  // Support tickets linked to this lead (via the new lead_id FK on support.tickets).
  const { data: leadTicketsData } = useTickets({ lead_id: id!, limit: 10000 })
  const leadTickets = leadTicketsData?.data || []
  const updateMutation = useUpdateLead()

  const LEAD_SOURCES = usePicklistOptions('lead', 'lead_source')
  const MARKETING_STATUSES = usePicklistOptions('lead', 'marketing_status__c')
  const BANT_BUDGET_OPTIONS = usePicklistOptions('lead', 'budget_status')
  const BANT_AUTHORITY_OPTIONS = usePicklistOptions('lead', 'authority_status')
  const BANT_NEED_OPTIONS = usePicklistOptions('lead', 'need_status')
  const BANT_TIMELINE_OPTIONS = usePicklistOptions('lead', 'timeline_status')

  const [isEditMode, setIsEditMode] = useState(false)
  const [notifyOwner, setNotifyOwner] = useState(true)
  const [editForm, setEditForm] = useState<Partial<Lead>>({})
  // Snapshot when edit opens, so Save sends only changed fields (avoids
  // resubmitting an unchanged owner_id, which the API rejects for non-admins).
  const [initialEditForm, setInitialEditForm] = useState<Partial<Lead>>({})
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [convertModalOpen, setConvertModalOpen] = useState(false)
  const [leadToConvert, setLeadToConvert] = useState<Lead | null>(null)
  const [createTaskModalOpen, setCreateTaskModalOpen] = useState(false)
  const [createEventModalOpen, setCreateEventModalOpen] = useState(false)
  const [createNoteModalOpen, setCreateNoteModalOpen] = useState(false)
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null)
  const [taskDetailModalOpen, setTaskDetailModalOpen] = useState(false)
  const [disqualifyModalOpen, setDisqualifyModalOpen] = useState(false)
  const [confirmConvertOpen, setConfirmConvertOpen] = useState(false)
  const [composeModalOpen, setComposeModalOpen] = useState(false)
  const [notePanelOpen, setNotePanelOpen] = useState(false)
  const [existingSessionId, setExistingSessionId] = useState<string | undefined>(undefined)
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null)
  const [noteDetailModalOpen, setNoteDetailModalOpen] = useState(false)
  const [editingNote, setEditingNote] = useState<Note | null>(null)
  const [editNoteModalOpen, setEditNoteModalOpen] = useState(false)
  const [editingSession, setEditingSession] = useState<NoteSession | null>(null)

  // Click-to-edit hook — must be called before any conditional returns to
  // keep hook order stable. The lambda closes over `handleEditClick` which
  // is defined later in the component.
  const { bindReadOnly } = useClickToEdit(isEditMode, () => handleEditClick())

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96" role="status" aria-live="polite">
        <div className="text-center">
          <div
            className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent motion-reduce:animate-[spin_1.5s_linear_infinite]"
            role="progressbar"
            aria-label="Loading lead details"
          ></div>
          <p className="mt-2 text-sm text-muted-foreground">Loading lead details...</p>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center justify-center h-96" role="alert">
        <p className="text-lg font-semibold text-destructive">Error loading lead</p>
        <p className="text-sm text-muted-foreground mt-2">
          {explainError(error, 'Lead not found')}
        </p>
        <Button
          onClick={() => navigate('/leads')}
          className="mt-4"
          aria-label="Return to leads list"
        >
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
          Back to Leads
        </Button>
      </div>
    )
  }

  const lead = data.lead
  const notes = data.notes || []

  // Calculate completeness score
  const importantFields = getImportantFields('lead')
  const completeness = calculateCompleteness(lead, importantFields)

  // Open the Internal Support form pre-filled with this lead's details and linked
  // back to the lead (lead_id). The request type is left for the user to choose.
  const handleCreateSupportTicket = () => {
    const params = new URLSearchParams()
    const name = `${lead.first_name || ''} ${lead.last_name || ''}`.trim()
    if (name) params.set('contact', name)
    if (lead.email) params.set('contact_email', lead.email)
    const phone = lead.phone || lead.mobile_phone
    if (phone) params.set('contact_phone', phone)
    if (lead.company) params.set('company_name', lead.company)
    const salesRep = lead.owner_id || currentUser?.id
    if (salesRep) params.set('sales_rep', salesRep)
    params.set('lead_id', lead.id)
    navigate(`/support-request?${params.toString()}`)
  }

  const getStatusBadgeVariant = (status: string) => {
    const lowerStatus = status?.toLowerCase() || ''
    if (lowerStatus === 'qualified') return 'success'
    if (lowerStatus === 'disqualified') return 'destructive'
    if (lowerStatus === 'working') return 'warning'
    return 'default'
  }

  const formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A'
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
  }

  const handleDeleteConfirm = async () => {
    const leadName = `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'this lead'
    try {
      await deleteLeadMutation.mutateAsync(lead.id)
      toast({ title: 'Lead deleted', description: `"${leadName}" has been deleted.` })
      setDeleteDialogOpen(false)
      navigate('/leads')
    } catch (error: any) {
      toast({
        title: 'Delete failed',
        description: explainError(error, 'Could not delete the lead.'),
        variant: 'destructive',
      })
    }
  }

  const handleEditClick = () => {
    const form = { ...lead }
    setEditForm(form)
    setInitialEditForm(form)
    setIsEditMode(true)
  }

  const handleCancelEdit = () => {
    setEditForm({})
    setFormErrors({})
    setIsEditMode(false)
  }

  const openConvertConfirmation = () => {
    setConfirmConvertOpen(true)
  }

  const handleConvertConfirmed = () => {
    setConfirmConvertOpen(false)
    setLeadToConvert(lead)
    setConvertModalOpen(true)
  }

  const handleStatusChange = async (newStatus: string) => {
    if (newStatus === 'Qualified') {
      openConvertConfirmation()
      return
    }
    if (newStatus === 'Disqualified') {
      setDisqualifyModalOpen(true)
      return
    }

    try {
      await updateMutation.mutateAsync({
        id: id!,
        data: { status: newStatus },
      })

      toast({
        title: 'Status Updated',
        description: `Lead status changed to ${newStatus}.`,
      })
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to update status')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  const handleDisqualifyConfirm = async (reason: string, details?: string) => {
    try {
      await updateMutation.mutateAsync({
        id: id!,
        data: {
          status: 'Disqualified',
          reason_for_disqualification__c: reason,
          disqualified_reason_details__c: details || '',
        },
      })

      toast({
        title: 'Lead Disqualified',
        description: `Lead disqualified: ${reason}`,
      })
      setDisqualifyModalOpen(false)
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to disqualify lead')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  const validate = (): boolean => {
    const errors: Record<string, string> = {}

    if (!editForm.last_name?.trim()) {
      errors.last_name = 'Last name is required'
    }
    if (!editForm.status) {
      errors.status = 'Status is required'
    }

    if (editForm.status === 'Disqualified' && !editForm.reason_for_disqualification__c) {
      errors.reason_for_disqualification__c = 'Disqualification reason is required'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSave = async () => {
    if (!validate()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields correctly.',
        variant: 'destructive',
      })
      return
    }

    // Send only the fields that actually changed, so an unchanged owner_id (or
    // any other guarded field) isn't resubmitted and rejected.
    const changed: Record<string, unknown> = {}
    for (const key of Object.keys(editForm)) {
      const k = key as keyof typeof editForm
      if (editForm[k] !== initialEditForm[k]) {
        changed[key] = editForm[k]
      }
    }

    if (Object.keys(changed).length === 0) {
      setIsEditMode(false)
      setFormErrors({})
      return
    }

    // Only carry the notify flag when the owner actually changed.
    if ('owner_id' in changed) {
      changed.notify_owner = notifyOwner
    }

    try {
      const result = await updateMutation.mutateAsync({
        id: id!,
        data: changed as Partial<Lead> & { notify_owner?: boolean },
      })

      const warnings = (result as { warnings?: string[] })?.warnings
      if (warnings?.length) {
        toast({
          title: 'Lead updated with warnings',
          description: warnings.join(' '),
          variant: 'destructive',
        })
      } else {
        toast({
          title: 'Success',
          description: 'Lead updated successfully.',
        })
      }

      setIsEditMode(false)
      setFormErrors({})
    } catch (error: any) {
      // Prefer the backend's specific message/details over the generic axios error.
      const data = error?.response?.data
      const errorMessage =
        (Array.isArray(data?.details) && data.details.length
          ? data.details.join(', ')
          : data?.message || data?.error) || explainError(error, 'Failed to update lead')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  const handleFieldChange = (field: keyof Lead, value: any) => {
    if (field === 'status' && value === 'Qualified') {
      openConvertConfirmation()
      return
    }
    if (field === 'status' && value === 'Disqualified') {
      setDisqualifyModalOpen(true)
      return
    }
    setEditForm((prev) => ({ ...prev, [field]: value }))
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  const handleDisqualifyConfirmEditMode = (reason: string, details?: string) => {
    setEditForm((prev) => ({
      ...prev,
      status: 'Disqualified',
      reason_for_disqualification__c: reason,
      disqualified_reason_details__c: details || '',
    }))
    setDisqualifyModalOpen(false)
  }

  const renderField = (
    label: string,
    field: keyof Lead,
    value: any,
    type: 'text' | 'email' | 'tel' | 'number' | 'textarea' | 'select' = 'text',
    options?: string[]
  ) => {
    // Role-based field permission: 'none' hides, 'read' is static (even in edit).
    const perm = leadFieldPerm(leadRole, field as string)
    if (perm === 'none') return null
    if (perm === 'read') {
      return <div className="text-sm mt-1 break-words">{value || 'N/A'}</div>
    }
    if (isEditMode) {
      if (type === 'select' && options) {
        return (
          <div>
            <Label htmlFor={field}>
              {label} {['last_name', 'status'].includes(field) && '*'}
            </Label>
            <Select
              value={(editForm[field] as string) || ''}
              onValueChange={(val) => handleFieldChange(field, val)}
            >
              <SelectTrigger id={field}>
                <SelectValue placeholder={`Select ${label}`} />
              </SelectTrigger>
              <SelectContent>
                {options.map((opt) => (
                  <SelectItem key={opt} value={opt}>
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {formErrors[field] && (
              <p className="text-sm text-destructive mt-1" role="alert">
                {formErrors[field]}
              </p>
            )}
          </div>
        )
      }

      if (type === 'textarea') {
        return (
          <div>
            <Label htmlFor={field}>{label}</Label>
            <Textarea
              id={field}
              value={(editForm[field] as string) || ''}
              onChange={(e) => handleFieldChange(field, e.target.value)}
              rows={3}
            />
          </div>
        )
      }

      return (
        <div>
          <Label htmlFor={field}>
            {label} {['last_name', 'status'].includes(field) && '*'}
          </Label>
          <Input
            id={field}
            type={type}
            value={(editForm[field] as string | number) || ''}
            onChange={(e) =>
              handleFieldChange(
                field,
                type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value
              )
            }
            aria-required={['last_name', 'status'].includes(field)}
            aria-invalid={!!formErrors[field]}
            aria-describedby={formErrors[field] ? `${field}-error` : undefined}
          />
          {formErrors[field] && (
            <p id={`${field}-error`} className="text-sm text-destructive mt-1" role="alert">
              {formErrors[field]}
            </p>
          )}
        </div>
      )
    }

    const readProps = bindReadOnly(field, label)
    return (
      <div
        {...readProps}
        className={`text-sm flex items-center justify-between gap-2 ${readProps.className}`}
      >
        <span className="min-w-0 break-words whitespace-pre-wrap">{value || 'N/A'}</span>
        <Pencil className="h-3.5 w-3.5 shrink-0 text-destructive" aria-hidden="true" />
      </div>
    )
  }

  const renderOwnerField = () => {
    if (isEditMode && canChangeOwner) {
      return (
        <div>
          <Label htmlFor="owner_id">Owner</Label>
          <Select
            value={editForm.owner_id || ''}
            onValueChange={(val) => handleFieldChange('owner_id', val)}
          >
            <SelectTrigger id="owner_id">
              <SelectValue placeholder="Select owner" />
            </SelectTrigger>
            <SelectContent>
              {usersData?.data?.map((user) => (
                <SelectItem key={user.id} value={user.id}>
                  {user.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2 mt-2">
            <Checkbox
              id="lead-notify_owner"
              checked={notifyOwner}
              onCheckedChange={(checked) => setNotifyOwner(checked === true)}
            />
            <Label htmlFor="lead-notify_owner" className="text-sm font-normal cursor-pointer">
              Notify new owner by email (only if owner changes)
            </Label>
          </div>
        </div>
      )
    }

    return <div className="text-sm">{lead.owner_name || 'Unassigned'}</div>
  }

  return (
    <div className="space-y-6" role="main" aria-label="Lead details">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/leads')}
            aria-label="Back to leads list"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          </Button>
          <CategoryBadge type="lead" />
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold tracking-tight">
                {lead.first_name} {lead.last_name}
              </h1>
              <CompletenessIndicator percentage={completeness} showLabel />
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {lead.company || 'No company'}
              {lead.title && ` • ${lead.title}`}
            </p>
          </div>
          {!isEditMode && !lead.is_converted && (
            <Button onClick={openConvertConfirmation} aria-label="Convert lead to opportunity">
              <TrendingUp className="mr-2 h-4 w-4" aria-hidden="true" />
              Convert
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isEditMode ? (
            <>
              {lead.rating && <Badge variant="secondary">{lead.rating}</Badge>}
              {lead.is_converted && <Badge variant="success">Converted</Badge>}
              <Button
                variant="default"
                onClick={() => setNotePanelOpen(true)}
                disabled={!!impersonatedUser}
                aria-label={
                  impersonatedUser
                    ? 'Read-only while impersonating'
                    : `Open call notes for ${lead.first_name} ${lead.last_name}`
                }
                title={impersonatedUser ? 'Read-only while impersonating' : undefined}
              >
                <FileText className="mr-2 h-4 w-4" aria-hidden="true" />
                Call Notes
              </Button>
              <Button
                variant="outline"
                onClick={() => setComposeModalOpen(true)}
                disabled={!lead.email}
                aria-label={
                  lead.email ? `Send email to ${lead.email}` : 'No email address available'
                }
              >
                <Send className="mr-2 h-4 w-4" aria-hidden="true" />
                Send Email
              </Button>
              <Button
                variant="outline"
                onClick={handleCreateSupportTicket}
                aria-label={`Create an internal support ticket for ${lead.first_name || ''} ${lead.last_name || ''}`.trim()}
              >
                <LifeBuoy className="mr-2 h-4 w-4" aria-hidden="true" />
                Support Ticket
              </Button>
              {!lead.is_converted && (
                <Button
                  variant="outline"
                  onClick={openConvertConfirmation}
                  aria-label="Convert lead to opportunity"
                >
                  <TrendingUp className="mr-2 h-4 w-4" aria-hidden="true" />
                  Convert
                </Button>
              )}
              <Button variant="outline" onClick={handleEditClick} aria-label="Edit lead">
                <Pencil className="mr-2 h-4 w-4" aria-hidden="true" />
                Edit
              </Button>
              {canDelete && (
                <Button
                  variant="destructive"
                  onClick={() => setDeleteDialogOpen(true)}
                  aria-label={`Delete lead ${lead.first_name || ''} ${lead.last_name || ''}`.trim()}
                >
                  <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
                  Delete
                </Button>
              )}
            </>
          ) : (
            <>
              <Button variant="outline" onClick={handleCancelEdit} aria-label="Cancel editing">
                <X className="mr-2 h-4 w-4" aria-hidden="true" />
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={updateMutation.isPending}
                aria-label="Save changes"
              >
                <Save className="mr-2 h-4 w-4" aria-hidden="true" />
                {updateMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Lead Stage Progress */}
      {!isEditMode && (
        <LeadStageProgress
          currentStatus={lead.status}
          onStatusChange={handleStatusChange}
          disabled={updateMutation.isPending}
        />
      )}

      {/* Website Inquiry — what the lead asked about (from the webform). Surfaced
          prominently here because reps were missing it in the small Lead Source subtext. */}
      {lead.lead_source_details__c && (
        <section aria-label="Website inquiry details">
          <Card className="border-primary/30 bg-primary/5">
            <CardHeader className="flex flex-row items-center gap-2 space-y-0 pb-2">
              <MessageSquareText className="h-4 w-4 text-primary" aria-hidden="true" />
              <CardTitle className="text-sm font-semibold">Website Inquiry</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="text-sm whitespace-pre-line text-foreground">
                {lead.lead_source_details__c}
              </p>
            </CardContent>
          </Card>
        </section>
      )}

      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Lead Source</CardTitle>
            <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">{lead.lead_source || 'N/A'}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Rating</CardTitle>
            <CheckCircle2 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">{lead.rating || 'N/A'}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Status</CardTitle>
            <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">
              <Badge variant={getStatusBadgeVariant(lead.status)} className="text-sm">
                {lead.status}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Owner</CardTitle>
            <User className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold truncate">{lead.owner_name || 'Unassigned'}</div>
          </CardContent>
        </Card>

        {lead.status !== 'Disqualified' && (
          <Card>
            <LeadScoreCard score={leadScore} isLoading={leadScoreLoading} />
          </Card>
        )}
      </div>

      {lead.status !== 'Disqualified' && leadScore && (
        <NextBestAction type="lead" leadScore={leadScore} />
      )}

      {/* Main Content with Tabs */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Lead Information (2/3 width) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Core lead details</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-0">
                <div className="py-2 border-b border-border">
                  <Label className="text-sm font-medium text-muted-foreground">First Name</Label>
                  {renderField('First Name', 'first_name', lead.first_name)}
                </div>
                <div className="py-2 border-b border-border">
                  <Label className="text-sm font-medium text-muted-foreground">Last Name *</Label>
                  {renderField('Last Name', 'last_name', lead.last_name)}
                </div>
                <div className="py-2 border-b border-border">
                  <Label className="text-sm font-medium text-muted-foreground">Company</Label>
                  {renderField('Company', 'company', lead.company)}
                </div>
                <div className="py-2 border-b border-border">
                  <Label className="text-sm font-medium text-muted-foreground">Title</Label>
                  {renderField('Title', 'title', lead.title)}
                </div>
                <div className="py-2 border-b border-border">
                  <Label className="text-sm font-medium text-muted-foreground">Status *</Label>
                  {renderField('Status', 'status', lead.status, 'select', [
                    'New',
                    'Working',
                    'Qualified',
                    'Disqualified',
                  ])}
                </div>
                <div className="py-2 border-b border-border">
                  <Label className="text-sm font-medium text-muted-foreground">Industry</Label>
                  {renderField('Industry', 'industry', lead.industry)}
                </div>
                <div className="py-2 border-b border-border">
                  <Label className="text-sm font-medium text-muted-foreground">Source</Label>
                  {renderField('Source', 'lead_source', lead.lead_source, 'select', LEAD_SOURCES)}
                </div>
                {leadFieldPerm(leadRole, 'marketing_status__c') !== 'none' && (
                  <div className="py-2 border-b border-border">
                    <Label className="text-sm font-medium text-muted-foreground">
                      Marketing Status
                    </Label>
                    {renderField(
                      'Marketing Status',
                      'marketing_status__c',
                      lead.marketing_status__c,
                      'select',
                      MARKETING_STATUSES
                    )}
                    {isEditMode &&
                      leadFieldPerm(leadRole, 'marketing_status__c') === 'edit' &&
                      editForm.marketing_status__c === 'Nurture' && (
                        <div className="mt-2">
                          <Label
                            htmlFor="marketing_recheck_date__c"
                            className="text-xs text-muted-foreground"
                          >
                            Recheck date
                          </Label>
                          <input
                            id="marketing_recheck_date__c"
                            type="date"
                            aria-describedby="marketing-recheck-help"
                            className="mt-1 block rounded border border-input bg-background px-2 py-1 text-sm"
                            value={
                              (editForm.marketing_recheck_date__c as string)?.split('T')[0] || ''
                            }
                            onChange={(e) =>
                              handleFieldChange('marketing_recheck_date__c', e.target.value)
                            }
                          />
                          <span id="marketing-recheck-help" className="sr-only">
                            Date for the BDR to re-evaluate this lead for MQL promotion.
                          </span>
                        </div>
                      )}
                  </div>
                )}
                <div className="py-2 border-b border-border">
                  <Label className="text-sm font-medium text-muted-foreground">Owner</Label>
                  {renderOwnerField()}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
              <CardDescription>Communication details</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-0">
                <div className="py-2 border-b border-border">
                  <Label className="text-sm font-medium text-muted-foreground">Email</Label>
                  {renderField('Email', 'email', lead.email, 'email')}
                </div>
                <div className="py-2 border-b border-border">
                  <Label className="text-sm font-medium text-muted-foreground">Phone</Label>
                  {isEditMode ? (
                    renderField('Phone', 'phone', lead.phone, 'tel')
                  ) : lead.phone ? (
                    <ClickToCallButton
                      phoneNumber={lead.phone}
                      whoId={lead.id}
                      whoType="Lead"
                      whoName={`${lead.first_name} ${lead.last_name}`}
                    />
                  ) : (
                    <div className="text-sm">N/A</div>
                  )}
                </div>
                <div className="py-2 border-b border-border">
                  <Label className="text-sm font-medium text-muted-foreground">Mobile Phone</Label>
                  {isEditMode ? (
                    renderField('Mobile Phone', 'mobile_phone', lead.mobile_phone, 'tel')
                  ) : lead.mobile_phone ? (
                    <ClickToCallButton
                      phoneNumber={lead.mobile_phone}
                      whoId={lead.id}
                      whoType="Lead"
                      whoName={`${lead.first_name} ${lead.last_name}`}
                    />
                  ) : (
                    <div className="text-sm">N/A</div>
                  )}
                </div>
                <div className="py-2 border-b border-border">
                  <Label className="text-sm font-medium text-muted-foreground">Website</Label>
                  {renderField('Website', 'website', lead.website)}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Company Information */}
          <Card>
            <CardHeader>
              <CardTitle>Company Information</CardTitle>
              <CardDescription>Organization details</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-0">
                <div className="py-2 border-b border-border">
                  <Label className="text-sm font-medium text-muted-foreground">
                    Annual Revenue
                  </Label>
                  {renderField(
                    'Annual Revenue',
                    'annual_revenue',
                    formatCurrency(lead.annual_revenue),
                    'number'
                  )}
                </div>
                <div className="py-2 border-b border-border">
                  <Label className="text-sm font-medium text-muted-foreground">
                    Number of Employees
                  </Label>
                  {renderField(
                    'Number of Employees',
                    'number_of_employees',
                    lead.number_of_employees,
                    'number'
                  )}
                </div>
                <div className="md:col-span-2 py-2 border-b border-border">
                  <Label className="text-sm font-medium text-muted-foreground">Address</Label>
                  {renderField('Address', 'address', lead.address, 'textarea')}
                </div>
                {lead.matched_account_id && (
                  <div className="md:col-span-2 py-2 border-b border-border">
                    <Label className="text-sm font-medium text-muted-foreground">
                      Matched Account
                    </Label>
                    <div className="mt-1">
                      <Link
                        to={`/accounts/${lead.matched_account_id}`}
                        className="text-sm font-medium text-primary hover:underline"
                      >
                        {lead.matched_account_name || 'View account'}
                      </Link>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Auto-linked by email domain
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Custom Fields */}
          <Card>
            <CardHeader>
              <CardTitle>Digital Accessibility Information</CardTitle>
              <CardDescription>Accessibility maturity and vendor details</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-0">
                <div className="py-2 border-b border-border">
                  <Label className="text-sm font-medium text-muted-foreground">
                    Accessibility Proficiency
                  </Label>
                  {renderField(
                    'Accessibility Proficiency',
                    'digital_accessibility_proficiency__c',
                    lead.digital_accessibility_proficiency__c
                  )}
                </div>
                <div className="py-2 border-b border-border">
                  <Label className="text-sm font-medium text-muted-foreground">
                    Digital Policy
                  </Label>
                  {renderField('Digital Policy', 'digital_policy__c', lead.digital_policy__c)}
                </div>
                <div className="py-2 border-b border-border">
                  <Label className="text-sm font-medium text-muted-foreground">
                    Current Vendor
                  </Label>
                  {renderField(
                    'Current Vendor',
                    'current_digital_accessibility_vendor__c',
                    lead.current_digital_accessibility_vendor__c
                  )}
                </div>
                <div className="py-2 border-b border-border">
                  <Label className="text-sm font-medium text-muted-foreground">Type of Shop</Label>
                  {renderField('Type of Shop', 'type_of_shop__c', lead.type_of_shop__c)}
                </div>
                <div className="py-2 border-b border-border">
                  <Label className="text-sm font-medium text-muted-foreground">
                    Number of Locations
                  </Label>
                  {renderField(
                    'Number of Locations',
                    'number_of_locations__c',
                    lead.number_of_locations__c,
                    'number'
                  )}
                </div>
                <div className="py-2 border-b border-border">
                  <Label className="text-sm font-medium text-muted-foreground">Role</Label>
                  {renderField('Role', 'role__c', lead.role__c)}
                </div>
                <div className="py-2 border-b border-border">
                  <Label className="text-sm font-medium text-muted-foreground">Campaign</Label>
                  {renderField('Campaign', 'campaign__c', lead.campaign__c)}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* BANT Qualification — carried to opportunity on conversion */}
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-3">
              <div>
                <CardTitle>BANT Qualification</CardTitle>
                <CardDescription>
                  Captured here and copied to the opportunity automatically when this lead is
                  converted.
                </CardDescription>
              </div>
              {!isEditMode && !lead.is_converted && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleEditClick}
                  aria-label="Edit BANT qualification"
                  className="shrink-0"
                >
                  <Pencil className="h-4 w-4 mr-2" aria-hidden="true" />
                  Edit
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(
                  [
                    {
                      key: 'budget__c',
                      statusKey: 'budget_status__c',
                      criterion: 'budget' as BantCriterion,
                      label: 'Budget',
                      options: BANT_BUDGET_OPTIONS,
                    },
                    {
                      key: 'authority__c',
                      statusKey: 'authority_status__c',
                      criterion: 'authority' as BantCriterion,
                      label: 'Authority',
                      options: BANT_AUTHORITY_OPTIONS,
                    },
                    {
                      key: 'need__c',
                      statusKey: 'need_status__c',
                      criterion: 'need' as BantCriterion,
                      label: 'Need',
                      options: BANT_NEED_OPTIONS,
                    },
                    {
                      key: 'timeline__c',
                      statusKey: 'timeline_status__c',
                      criterion: 'timeline' as BantCriterion,
                      label: 'Timeline',
                      options: BANT_TIMELINE_OPTIONS,
                    },
                  ] as const
                ).map(({ key, statusKey, criterion, label, options }) => {
                  const statusValue = (lead as any)[statusKey] as string | undefined
                  const notesValue = (lead as any)[key] as string | undefined
                  return (
                    <div key={key} className="rounded-md border p-3">
                      <div className="text-sm font-semibold mb-2">{label}</div>
                      {isEditMode ? (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div>
                            <Label className="text-xs text-muted-foreground">Status</Label>
                            {renderField(
                              `${label} Status`,
                              statusKey as keyof Lead,
                              statusValue,
                              'select',
                              options
                            )}
                          </div>
                          <div className="md:col-span-2">
                            <Label className="text-xs text-muted-foreground">Notes</Label>
                            {renderField(
                              `${label} Notes`,
                              key as keyof Lead,
                              notesValue,
                              'textarea'
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <div className="flex items-baseline gap-2">
                            <span className="text-xs font-medium text-muted-foreground">
                              Status:
                            </span>
                            {statusValue ? (
                              <Badge variant={getBantStatusVariant(criterion, statusValue)}>
                                {statusValue}
                              </Badge>
                            ) : (
                              <span className="text-sm text-muted-foreground">Not set</span>
                            )}
                          </div>
                          <div>
                            <span className="text-xs font-medium text-muted-foreground">
                              Notes:{' '}
                            </span>
                            <span className="text-sm whitespace-pre-wrap">
                              {notesValue || 'N/A'}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>

          {/* Mailchimp: subscription state + soft bounces (read-only) and tags
              (editable by Marketing/Admin). */}
          <MailchimpContactSection
            contact={lead}
            entityType="lead"
            canEditTags={(leadRole === 'Marketing' || leadRole === 'Admin') && !impersonatedUser}
          />

          {/* Disqualification Information - only shown when lead is Disqualified */}
          {(lead.status === 'Disqualified' ||
            (isEditMode && editForm.status === 'Disqualified')) && (
            <Card>
              <CardHeader>
                <CardTitle>Disqualification Information</CardTitle>
                <CardDescription>Reason and details for disqualification</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-0">
                  <div className="py-2 border-b border-border">
                    <Label className="text-sm font-medium text-muted-foreground">Reason *</Label>
                    {renderField(
                      'Reason *',
                      'reason_for_disqualification__c',
                      lead.reason_for_disqualification__c,
                      'select',
                      [
                        'Non-Responsive',
                        'Other',
                        'No Interest',
                        'Already have provider',
                        'Not Applicable',
                      ]
                    )}
                  </div>
                  <div className="py-2 border-b border-border">
                    <Label className="text-sm font-medium text-muted-foreground">Details</Label>
                    {renderField(
                      'Details',
                      'disqualified_reason_details__c',
                      lead.disqualified_reason_details__c,
                      'textarea'
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Additional Information */}
          <Card>
            <CardHeader>
              <CardTitle>Additional Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-0">
                <div className="py-2 border-b border-border">
                  <Label className="text-sm font-medium text-muted-foreground">Description</Label>
                  {renderField('Description', 'description', lead.description, 'textarea')}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Related Data Tabs (1/3 width) */}
        <div className="lg:col-span-1">
          <Card>
            <Tabs defaultValue="activity" className="w-full">
              <CardHeader className="pb-3">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="activity">
                    <Calendar className="h-4 w-4 mr-1" aria-hidden="true" />
                    Activity
                  </TabsTrigger>
                  <TabsTrigger value="emails">
                    <Mail className="h-4 w-4 mr-1" aria-hidden="true" />
                    Emails
                  </TabsTrigger>
                  <TabsTrigger value="notes">
                    <FileText className="h-4 w-4 mr-1" aria-hidden="true" />
                    Notes ({notes.length + (noteSessions?.length ?? 0)})
                  </TabsTrigger>
                  <TabsTrigger value="history">
                    <History className="h-4 w-4 mr-1" aria-hidden="true" />
                    History
                  </TabsTrigger>
                </TabsList>
              </CardHeader>
              <CardContent className="pt-0">
                <TabsContent value="activity" className="mt-0">
                  <div className="flex items-center justify-end mb-3 gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCreateTaskModalOpen(true)}
                      aria-label="Create new task for this lead"
                    >
                      <Plus className="h-3 w-3 mr-1" aria-hidden="true" />
                      New Task
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCreateEventModalOpen(true)}
                      aria-label="Create new event for this lead"
                    >
                      <Plus className="h-3 w-3 mr-1" aria-hidden="true" />
                      New Event
                    </Button>
                  </div>
                  <ActivityTimeline entityId={id!} entityType="Lead" />
                </TabsContent>

                <TabsContent value="emails" className="mt-0">
                  <RelatedEmails entityType="lead" entityId={id!} maxHeight="400px" />
                </TabsContent>

                <TabsContent value="notes" className="mt-0">
                  <div className="flex justify-end mb-3">
                    <Button
                      size="sm"
                      onClick={() => {
                        setExistingSessionId(undefined)
                        setNotePanelOpen(true)
                      }}
                      aria-label="Create new note"
                    >
                      <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                      New Note
                    </Button>
                  </div>
                  <MergedNotesList
                    entityType="Lead"
                    entityId={lead.id}
                    legacyNotes={notes}
                    currentUserId={currentUser?.id}
                    currentUserType={currentUser?.user_type}
                    onOpenSession={(sessionId) => {
                      setExistingSessionId(sessionId)
                      setNotePanelOpen(true)
                    }}
                    onOpenLegacyNote={(note) => {
                      setSelectedNoteId(note.id)
                      setNoteDetailModalOpen(true)
                    }}
                    onEditNote={(note) => {
                      setEditingNote(note)
                      setEditNoteModalOpen(true)
                    }}
                    onDeleteNote={(note) => {
                      deleteNoteMutation.mutate(note.id)
                    }}
                    onEditSession={(session) => {
                      setEditingSession(session)
                      setExistingSessionId(session.id)
                      setNotePanelOpen(true)
                    }}
                    onDeleteSession={(session) => {
                      deleteSessionMutation.mutate(session.id)
                    }}
                  />
                </TabsContent>

                <TabsContent value="history" className="mt-0">
                  <HistoryTimeline entityType="Lead" entityId={id!} />
                </TabsContent>
              </CardContent>
            </Tabs>
          </Card>
        </div>
      </div>

      {/* Support Tickets linked to this lead (via lead_id) */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <LifeBuoy className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              Support Tickets{leadTickets.length > 0 ? ` (${leadTickets.length})` : ''}
            </CardTitle>
            <Button
              size="sm"
              variant="outline"
              onClick={handleCreateSupportTicket}
              aria-label="Create an internal support ticket for this lead"
            >
              <Plus className="h-3 w-3 mr-1" aria-hidden="true" />
              New Ticket
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {leadTickets.length > 0 ? (
            leadTickets.map((ticket) => (
              <Card key={ticket.id} className="p-3">
                <div className="space-y-2">
                  <div className="font-medium flex flex-wrap items-center gap-2">
                    <Headphones className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                    <Link to={`/tickets/${ticket.id}`} className="text-primary hover:underline">
                      GRK-{ticket.ticket_number}: {ticket.subject}
                    </Link>
                    <Badge
                      variant={ticketStatusVariants[ticket.status] || 'secondary'}
                      className="text-xs"
                    >
                      {ticket.status}
                    </Badge>
                    <Badge
                      variant={ticketPriorityVariants[ticket.priority] || 'outline'}
                      className="text-xs"
                    >
                      {ticket.priority}
                    </Badge>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      {ticket.category || 'Support'} • Owner: {ticket.owner_name || 'Unassigned'}
                    </span>
                    <span className="text-muted-foreground text-xs">
                      {ticket.created_date ? formatDate(ticket.created_date) : ''}
                    </span>
                  </div>
                </div>
              </Card>
            ))
          ) : (
            <div className="text-center text-sm text-muted-foreground py-6">
              No support tickets linked to this lead yet.
            </div>
          )}
        </CardContent>
      </Card>

      <EditActionBar
        isEditMode={isEditMode}
        onSave={handleSave}
        onCancel={handleCancelEdit}
        isSaving={updateMutation.isPending}
      />

      <ConvertLeadModal
        lead={leadToConvert}
        open={convertModalOpen}
        onOpenChange={setConvertModalOpen}
      />

      <CreateTaskModal
        open={createTaskModalOpen}
        onOpenChange={setCreateTaskModalOpen}
        relatedToId={lead.id}
        relatedToType="Lead"
        relatedToName={
          `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || lead.company || 'Lead'
        }
        ownerId={lead.owner_id}
      />

      <CreateEventModal
        open={createEventModalOpen}
        onOpenChange={setCreateEventModalOpen}
        relatedToId={lead.id}
        relatedToType="Lead"
        relatedToName={
          `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || lead.company || 'Lead'
        }
        ownerId={lead.owner_id}
      />

      <CreateNoteModal
        open={createNoteModalOpen}
        onOpenChange={setCreateNoteModalOpen}
        parentId={lead.id}
        parentName={`${lead.first_name || ''} ${lead.last_name}`.trim()}
      />

      <TaskDetailModal
        taskId={editingTaskId}
        open={taskDetailModalOpen}
        onOpenChange={(open) => {
          setTaskDetailModalOpen(open)
          if (!open) setEditingTaskId(null)
        }}
      />

      <DisqualifyLeadModal
        open={disqualifyModalOpen}
        onOpenChange={setDisqualifyModalOpen}
        onConfirm={isEditMode ? handleDisqualifyConfirmEditMode : handleDisqualifyConfirm}
        isPending={!isEditMode && updateMutation.isPending}
      />

      <AlertDialog open={confirmConvertOpen} onOpenChange={setConfirmConvertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Convert Lead?</AlertDialogTitle>
            <AlertDialogDescription>
              Qualifying this lead will start the conversion process. This will create an Account,
              Contact, and optionally an Opportunity. Do you want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel aria-label="Cancel conversion">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConvertConfirmed}
              aria-label="Continue with lead conversion"
            >
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Lead confirmation (Admin/Super Admin only) */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent aria-describedby="delete-lead-description">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Lead</AlertDialogTitle>
            <AlertDialogDescription id="delete-lead-description">
              Are you sure you want to delete the lead "
              {`${lead.first_name || ''} ${lead.last_name || ''}`.trim()}"? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel aria-label="Cancel delete operation">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleteLeadMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              aria-label="Confirm delete lead"
            >
              {deleteLeadMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Compose Email Modal */}
      <ComposeEmailModal
        open={composeModalOpen}
        onOpenChange={setComposeModalOpen}
        defaultTo={lead.email}
      />

      {/* Note Detail Modal */}
      <NoteDetailModal
        noteId={selectedNoteId}
        open={noteDetailModalOpen}
        onOpenChange={(open) => {
          setNoteDetailModalOpen(open)
          if (!open) setSelectedNoteId(null)
        }}
        currentUserId={currentUser?.id}
        currentUserType={currentUser?.user_type}
        onEdit={(note) => {
          setSelectedNoteId(null)
          setNoteDetailModalOpen(false)
          setEditingNote(note)
          setEditNoteModalOpen(true)
        }}
        onDelete={(note) => {
          deleteNoteMutation.mutate(note.id)
          setNoteDetailModalOpen(false)
        }}
      />

      {/* Edit Note Modal */}
      <EditNoteModal
        open={editNoteModalOpen}
        onOpenChange={setEditNoteModalOpen}
        note={editingNote}
        parentId={id!}
      />

      {/* Note Session Panel */}
      <NoteSessionPanel
        entityType="Lead"
        entityId={lead.id}
        entityName={`${lead.first_name} ${lead.last_name}`}
        open={notePanelOpen}
        onOpenChange={(open) => {
          setNotePanelOpen(open)
          if (!open) {
            setExistingSessionId(undefined)
            setEditingSession(null)
          }
        }}
        readOnly={!!impersonatedUser}
        existingSessionId={existingSessionId}
        allowEdit={editingSession?.id === existingSessionId}
      />
    </div>
  )
}
